import React from "react";
import { NavLink, useLocation, useOutlet } from "react-router-dom";
import { NavbarSub } from "../../libs/shared";
import { usersGetbyToken } from "../../libs/apis";
import { useRouterStore } from "../../libs/stores";
import { DynamicIcon, extractModuleBase, findClosestModuleByPath, normalizePath } from "../../libs/helpers";
import { COLOR_PRIMARY } from "../../libs/constants";
import { CSSTransition, SwitchTransition } from "react-transition-group";
import { message, Spin } from "antd";
import { LoadingPage } from "./Loading";
import { getValidRoutes } from "../../libs/helpers";
import "../css/generic.css";

const LazyPage = React.lazy(() => import("./Page"));



export const GenericPage = () => {
  const location = useLocation();
  const currentOutlet = useOutlet();
  
  // OPTIMIZACIÓN: Solo obtener lo que necesitamos del store
  const validateModulesFromApi = useRouterStore((state) => state.validateModulesFromApi);
  const resetOptions = useRouterStore((state) => state.resetOptions);
  const setOptions = useRouterStore((state) => state.setOptions);
  
  // OPTIMIZACIÓN: Usar selectors específicos para evitar re-renders
  const modules = useRouterStore((state) => state.modules);
  
  const options = useRouterStore((state) => state.options);
  
  const isLoading = useRouterStore((state) => state.isLoading);
  const previousModuleBase = useRouterStore((state) => state.previousModuleBase);
  const setPreviousModuleBase = useRouterStore((state) => state.setPreviousModuleBase);
  
  // OPTIMIZACIÓN: Memoizar validRoutes
  const validRoutesMap = React.useMemo(() => getValidRoutes(), []);

  // OPTIMIZACIÓN: Estado local para controlar cuándo validar
  const [hasValidatedInitially, setHasValidatedInitially] = React.useState(false);

  // OPTIMIZACIÓN: Memoizar la función de validación de usuario
  const handleValidate = React.useCallback(async () => { 
    try {
      await usersGetbyToken();
    } catch (err: any) {
      message.error(err.toString());
    }
  }, []);

  // OPTIMIZACIÓN: Separar la validación inicial de la actualización de opciones
  React.useEffect(() => {
    if (!hasValidatedInitially) {
      const initializeApp = async () => {
        await handleValidate();
        if (modules.length === 0) {
          await validateModulesFromApi();
        }
        setHasValidatedInitially(true);
      };
      initializeApp();
    }
  }, [hasValidatedInitially, handleValidate, validateModulesFromApi, modules.length]);

  // OPTIMIZACIÓN: Actualizar opciones cuando cambie la ruta o módulos
  React.useEffect(() => {
    if (!hasValidatedInitially || modules.length === 0) return;

    const currentPath = location.pathname;
    const moduleBase = extractModuleBase(currentPath);


    // Actualizar el módulo base actual
    if (previousModuleBase !== moduleBase) {
      setPreviousModuleBase(moduleBase);
    }

    const updateOptions = () => {
      const closestModule = findClosestModuleByPath(
        modules,
        normalizePath(currentPath)
      );


      if (closestModule?.options?.length) {
        const validRoutes = validRoutesMap[closestModule.to] || new Set();
        const validOptions = closestModule.options.filter((option) =>
          validRoutes.size > 0 ? validRoutes.has(option.to) : true
        );
        
        
        // Siempre actualizar las opciones para asegurar que se muestren
        setOptions(validOptions);
      } else {
        setOptions([]);
      }
    };

    // Usar setTimeout para asegurar que la actualización se ejecute después del render
    const timer = setTimeout(() => {
      updateOptions();
    }, 50);

    return () => clearTimeout(timer);
  }, [
    location.pathname,
    modules,
    hasValidatedInitially,
    validRoutesMap,
    setOptions,
    setPreviousModuleBase,
    previousModuleBase
  ]);

  // OPTIMIZACIÓN: Memoizar los elementos de navegación
  const navItems = React.useMemo(
    () =>
      options
        .filter((el) => el.show)
        .map((el) => (
          <div key={`option_${el.id}_${el.to}`}>
            <NavLink to={el.to}>
              {({ isActive }) => (
                <button className={isActive ? "button-active" : "button"}>
                  <DynamicIcon
                    icon={el.icon}
                    lib={el.iconLibrary}
                    color={COLOR_PRIMARY}
                  />
                  {el.name}
                </button>
              )}
            </NavLink>
          </div>
        )),
    [options]
  );

  // OPTIMIZACIÓN: Memoizar la barra de navegación
  const navigationBar = React.useMemo(() => (
    <NavbarSub>
      <div className="div-nav" key={`nav_${options.length}`}>
        {isLoading || options.length === 0 ? (
          <div className="div-loading">
            <Spin size="small" />
          </div>
        ) : (
          navItems
        )}
      </div>
    </NavbarSub>
  ), [isLoading, options.length, navItems]);

  // OPTIMIZACIÓN: Memoizar el contenido principal
  const pageContent = React.useMemo(() => (
    <SwitchTransition mode="out-in">
      <CSSTransition
        key={location.pathname}
        timeout={100}
        classNames="page"
        unmountOnExit
      >
        <React.Suspense fallback={<LoadingPage />}>
          <LazyPage currentOutlet={currentOutlet} />
        </React.Suspense>
      </CSSTransition>
    </SwitchTransition>
  ), [location.pathname, currentOutlet]);

  return (
    <div className="root" key={`root_${location.pathname}`}>
      {navigationBar}
      {pageContent}
    </div>
  );
};